import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";
import "./rules.css";
import "../game-selector/gameSelector.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";
import AdminGameContentHeader from "../../components/AdminGameContentHeader";
import { getActiveGame, getGameDisplayName } from "../../../utils/gameHelpers";
import GameBrandingSection from "./GameBrandingSection";

function s3AssetUrl(base, filename) {
  if (!base || !filename) return "";
  return base + encodeURIComponent(String(filename)).replace(/%2F/g, "/");
}

const LANDING_TITLE_MAX_LEN = 100;
const GAME_SUB_TITLE_MAX_LEN = 300;
const LABEL_MAX_LEN = 50;
const MIN_UPLOAD_COUNT = 1;
const MAX_UPLOAD_COUNT = 100;
const MIN_BADGE_SHARE_LIMIT = 1;
const MAX_BADGE_SHARE_LIMIT = 100;

function clampUploadCount(value) {
  const n = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return MIN_UPLOAD_COUNT;
  return Math.max(MIN_UPLOAD_COUNT, Math.min(MAX_UPLOAD_COUNT, n));
}

function clampBadgeShareLimit(value) {
  const n = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return MIN_BADGE_SHARE_LIMIT;
  return Math.max(MIN_BADGE_SHARE_LIMIT, Math.min(MAX_BADGE_SHARE_LIMIT, n));
}

function parseStatusOpen(raw, defaultOpen = true) {
  const s = String(raw ?? (defaultOpen ? "open" : "closed")).trim().toLowerCase();
  return s !== "closed" && s !== "0" && s !== "false";
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);
  const activeGame = getActiveGame(data);
  const isUploadModerationGame = Boolean(activeGame);

  const [rules, setRules] = useState([""]);

  const [landingPageTitle, setLandingPageTitle] = useState("");
  const [gamePageSubTitle, setGamePageSubTitle] = useState("");
  const [thankYouPage, setThankYouPage] = useState("");
  const [thankYouGif, setThankYouGif] = useState("");
  const [isUploadingGif, setIsUploadingGif] = useState(false);
  const gifInputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [votingOpen, setVotingOpen] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(true);
  const [uploadCount, setUploadCount] = useState(MIN_UPLOAD_COUNT);
  const [savingYogaSettings, setSavingYogaSettings] = useState(false);
  const yogaSettingsHydratedRef = useRef(false);

  const [labelName, setLabelName] = useState("");
  const [labelColleagueName, setLabelColleagueName] = useState("");
  const [labelColleagueEmail, setLabelColleagueEmail] = useState("");
  const [labelBadge, setLabelBadge] = useState("");

  const [badgesShareStatus, setBadgesShareStatus] = useState(true);
  const [badgesShareMultiple, setBadgesShareMultiple] = useState(false);
  const [badgesShareLimit, setBadgesShareLimit] = useState(MIN_BADGE_SHARE_LIMIT);
  const [uploadApprovalRequired, setUploadApprovalRequired] = useState(true);

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#ffffff",
    button_backgroundcolor: "#000000",
    like_box_text_color: "#088e01",
    like_box_background_color: "#088e01",
    quiz_question_text_color: "#1a1a1a",
    quiz_question_background_color: "#ffffff",
    quiz_option_text_color: "#0d1b5c",
    quiz_option_background_color: "#eceff1",
    rules_text_color: "#333333",
  });

  useEffect(() => {
    if (!data) return;
    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    setColors((prev) => ({
      text_color: (source?.text_color ?? data.text_color) ?? prev.text_color,
      button_color: (source?.button_color ?? data.button_color) ?? prev.button_color,
      button_backgroundcolor:
        (source?.button_backgroundcolor ?? data.button_backgroundcolor) ?? prev.button_backgroundcolor,
      like_box_text_color:
        (source?.like_box_text_color ?? data.like_box_text_color) ?? prev.like_box_text_color,
      like_box_background_color:
        (source?.like_box_background_color ?? data.like_box_background_color) ?? prev.like_box_background_color,
      quiz_question_text_color:
        ("quiz_question_text_color" in data
          ? data.quiz_question_text_color
          : prev.quiz_question_text_color) ?? prev.quiz_question_text_color,
      quiz_question_background_color:
        ("quiz_question_background_color" in data
          ? data.quiz_question_background_color
          : prev.quiz_question_background_color) ?? prev.quiz_question_background_color,
      quiz_option_text_color:
        ("quiz_option_text_color" in data ? data.quiz_option_text_color : prev.quiz_option_text_color) ??
        prev.quiz_option_text_color,
      quiz_option_background_color:
        ("quiz_option_background_color" in data
          ? data.quiz_option_background_color
          : prev.quiz_option_background_color) ?? prev.quiz_option_background_color,
      rules_text_color:
        data.rules_text_color ??
        source?.text_color ??
        data.text_color ??
        prev.rules_text_color,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // fetch questions & theme on mount
  useEffect(() => {
    yogaSettingsHydratedRef.current = false;
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    // Only overwrite when the key exists on the payload. If fetchThemeData returns a
    // partial object (missing keys), do not use undefined → "" or we wipe a filename
    // just set by upload and abort the in-flight S3 image (NS_BINDING_ABORTED).
    if ("game_page_title" in data || "landing_page_title" in data) {
      const t = String(data.game_page_title ?? data.landing_page_title ?? "");
      setLandingPageTitle(t.slice(0, LANDING_TITLE_MAX_LEN));
    }
    if ("game_page_sub_title" in data) {
      setGamePageSubTitle(
        String(data.game_page_sub_title ?? "").slice(0, GAME_SUB_TITLE_MAX_LEN)
      );
    }
    if ("custom_text_thank_you_page" in data) {
      setThankYouPage(data.custom_text_thank_you_page ?? "");
    }
    if ("thank_you_page" in data) setThankYouGif(data.thank_you_page ?? "");
    if ("lable_name_text" in data) {
      setLabelName(String(data.lable_name_text ?? "").slice(0, LABEL_MAX_LEN));
    }
    if ("lable_colleague_name_text" in data) {
      setLabelColleagueName(String(data.lable_colleague_name_text ?? "").slice(0, LABEL_MAX_LEN));
    }
    if ("lable_colleague_email_text" in data) {
      setLabelColleagueEmail(String(data.lable_colleague_email_text ?? "").slice(0, LABEL_MAX_LEN));
    }
    if ("lable_badge_text" in data) {
      setLabelBadge(String(data.lable_badge_text ?? "").slice(0, LABEL_MAX_LEN));
    }
    if ("voting_status" in data) setVotingOpen(parseStatusOpen(data.voting_status, true));
    if ("badges_share_status" in data) {
      setBadgesShareStatus(parseStatusOpen(data.badges_share_status, true));
    }
    if ("badges_share_multiple" in data) {
      setBadgesShareMultiple(String(data.badges_share_multiple) === "true");
    }
    if ("badges_share_limit" in data) {
      setBadgesShareLimit(clampBadgeShareLimit(data.badges_share_limit));
    }
    if ("upload_approval_required" in data) {
      setUploadApprovalRequired(String(data.upload_approval_required).toLowerCase() !== "false");
    }
    if ("upload_status" in data) setUploadOpen(parseStatusOpen(data.upload_status, true));
    if ("upload_count" in data) setUploadCount(clampUploadCount(data.upload_count));
    yogaSettingsHydratedRef.current = true;
  }, [data]);

  const buildYogaSettingsData = useCallback(
    (overrides = {}) => {
      const vOpen = overrides.votingOpen ?? votingOpen;
      const uOpen = overrides.uploadOpen ?? uploadOpen;
      const uCount = overrides.uploadCount ?? uploadCount;
      return {
        voting_status: vOpen ? "open" : "closed",
        upload_status: uOpen ? "open" : "closed",
        upload_count: uOpen ? clampUploadCount(uCount) : MIN_UPLOAD_COUNT,
      };
    },
    [votingOpen, uploadOpen, uploadCount]
  );

  const persistYogaSettings = useCallback(
    async (overrides = {}) => {
      if (!currentTheme || !adminToken) {
        Swal.fire("No theme", "Select a theme before changing yoga settings.", "warning");
        return false;
      }
      setSavingYogaSettings(true);
      try {
        await updateThemeData({
          payload: {
            data: buildYogaSettingsData(overrides),
            currentTheme,
          },
          token: adminToken,
        });
        await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
        return true;
      } catch (e) {
        Swal.fire("Error", e.message || "Could not save yoga settings.", "error");
        await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
        return false;
      } finally {
        setSavingYogaSettings(false);
      }
    },
    [adminToken, buildYogaSettingsData, currentTheme, dispatch]
  );

  const handleVotingChange = async (checked) => {
    setVotingOpen(checked);
    if (!yogaSettingsHydratedRef.current) return;
    await persistYogaSettings({ votingOpen: checked });
  };

  const handleUploadChange = async (checked) => {
    setUploadOpen(checked);
    if (!yogaSettingsHydratedRef.current) return;
    await persistYogaSettings({ uploadOpen: checked });
  };

  const handleUploadCountChange = (value) => {
    if (value === "") {
      setUploadCount("");
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return;
    setUploadCount(parsed);
  };

  const handleUploadCountBlur = async () => {
    if (!uploadOpen || !yogaSettingsHydratedRef.current) return;
    if (uploadCount === "") {
      setUploadCount(MIN_UPLOAD_COUNT);
      await persistYogaSettings({ uploadCount: MIN_UPLOAD_COUNT });
      return;
    }
    const clamped = clampUploadCount(uploadCount);
    setUploadCount(clamped);
    await persistYogaSettings({ uploadCount: clamped });
  };

  const persistUploadApproval = async (required) => {
    if (!currentTheme || !adminToken) {
      Swal.fire("No theme", "Select a theme before changing Auto Approve.", "warning");
      return false;
    }
    try {
      await updateThemeData({
        payload: {
          currentTheme,
          data: {
            active_game: activeGame,
            upload_approval_required: required ? "true" : "false",
          },
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      return true;
    } catch (e) {
      Swal.fire("Error", e.message || "Could not save Auto Approve.", "error");
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      return false;
    }
  };

  const handleAutoApproveChange = async (checked) => {
    const required = !checked;
    setUploadApprovalRequired(required);
    if (!yogaSettingsHydratedRef.current) return;
    await persistUploadApproval(required);
  };

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 200) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 200 characters. Extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 7) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 7 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const S3 = process.env.REACT_APP_S3_PATH || "";

  const handleGifUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "gif") {
      Swal.fire("Invalid file", "Only GIF files are allowed.", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("File too large", "Maximum 5 MB.", "error");
      return;
    }

    setIsUploadingGif(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("target", "thank_you_page");
    fd.append("themeName", String(currentTheme));

    try {
      const res = await fetch(apiUrl("/uploadSpotImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");

      setThankYouGif(json.filename || "");
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Uploaded", "Thank you GIF updated.", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed.", "error");
    }
    setIsUploadingGif(false);
    if (gifInputRef.current) gifInputRef.current.value = "";
  };

  const handleLandingPageTitleChange = (val) => {
    let next = val;
    if (next.length > LANDING_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Game page title can be at most ${LANDING_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, LANDING_TITLE_MAX_LEN);
    }
    setLandingPageTitle(next);
  };

  const handleThankYouPageChange = (val) => {
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Thank you page message can be at most 300 characters. Extra text has been trimmed.",
        "warning"
      );
      val = val.slice(0, 300);
    }
    setThankYouPage(val);
  };

  const handleGamePageSubTitleChange = (val) => {
    let next = val;
    if (next.length > GAME_SUB_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Game page sub title can be at most ${GAME_SUB_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, GAME_SUB_TITLE_MAX_LEN);
    }
    setGamePageSubTitle(next);
  };

  const handleLabelChange = (setter) => (val) => {
    setter(String(val ?? "").slice(0, LABEL_MAX_LEN));
  };

  const handleBadgeShareLimitChange = (value) => {
    if (value === "") {
      setBadgesShareLimit("");
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return;
    setBadgesShareLimit(parsed);
  };

  const handleBadgeShareLimitBlur = () => {
    setBadgesShareLimit(clampBadgeShareLimit(badgesShareLimit));
  };

  // --- submit all ---
  const handleSubmit = async () => {
    if (String(landingPageTitle ?? "").length > LANDING_TITLE_MAX_LEN) {
      Swal.fire(
        "Invalid title",
        `Game page title must be at most ${LANDING_TITLE_MAX_LEN} characters.`,
        "warning"
      );
      return;
    }
    const trimmedRules = rules.map((r) => String(r ?? "").trim());
    if (trimmedRules.some((r) => r === "")) {
      Swal.fire(
        "Incomplete rules",
        "Please add text to every rule, or remove empty rows using the × button before saving.",
        "warning"
      );
      return;
    }
    if (trimmedRules.length === 0) {
      Swal.fire(
        "Rules required",
        "Please add at least one rule with text before saving.",
        "warning"
      );
      return;
    }
    const normalizedBadgeShareLimit = clampBadgeShareLimit(badgesShareLimit);

    const payload = {
      data: {
        rules: trimmedRules,
        game_page_title: String(landingPageTitle ?? "").slice(0, LANDING_TITLE_MAX_LEN),
        game_page_sub_title: String(gamePageSubTitle ?? "").slice(0, GAME_SUB_TITLE_MAX_LEN),
        custom_text_thank_you_page: thankYouPage,
        lable_name_text: String(labelName ?? "").slice(0, LABEL_MAX_LEN),
        lable_colleague_name_text: String(labelColleagueName ?? "").slice(0, LABEL_MAX_LEN),
        lable_colleague_email_text: String(labelColleagueEmail ?? "").slice(0, LABEL_MAX_LEN),
        lable_badge_text: String(labelBadge ?? "").slice(0, LABEL_MAX_LEN),
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_backgroundcolor: colors.button_backgroundcolor,
        like_box_text_color: colors.like_box_text_color,
        like_box_background_color: colors.like_box_background_color,
        quiz_question_text_color: colors.quiz_question_text_color,
        quiz_question_background_color: colors.quiz_question_background_color,
        quiz_option_text_color: colors.quiz_option_text_color,
        quiz_option_background_color: colors.quiz_option_background_color,
        rules_text_color: colors.rules_text_color,
        voting_status: votingOpen ? "open" : "closed",
        badges_share_status: badgesShareStatus ? "true" : "false",
        badges_share_multiple: badgesShareMultiple ? "true" : "false",
        badges_share_limit: badgesShareMultiple ? normalizedBadgeShareLimit : 1,
        active_game: activeGame,
        upload_approval_required: uploadApprovalRequired ? "true" : "false",
        upload_status: uploadOpen ? "open" : "closed",
        upload_count: uploadOpen ? clampUploadCount(uploadCount) : MIN_UPLOAD_COUNT,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setTimeout(() => navigate("/admin/customize-spot"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <AdminGameContentHeader themeData={data} section="rules" />

          <div className="back-button-holder">
              <button
                className="back-button"
                onClick={() => navigate("/admin/")}
              >
              <i className="fa-solid fa-arrow-left"></i> Back
              </button>
          </div>

      <div className="rules-page-new">
        <GameBrandingSection />
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            {/* <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Basic Theme Details
                </div>
                <div className="form-group-v1">
                  <label>Game page title</label>
                  <textarea
                    value={landingPageTitle}
                    onChange={(e) => handleLandingPageTitleChange(e.target.value)}
                    placeholder="Game page title"
                    className="form-control rules-textarea"
                    rows={2}
                    maxLength={LANDING_TITLE_MAX_LEN}
                  />
                  <div className="code">
                    * Maximum {LANDING_TITLE_MAX_LEN} characters.
                  </div>
                </div>

                <div className="form-group-v1">
                  <label>Thank you page message</label>
                  <textarea
                    value={thankYouPage}
                    onChange={(e) => handleThankYouPageChange(e.target.value)}
                    placeholder="Thank you page message"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={300}
                  />
                  <div className="code">* Maximum 300 characters.</div>
                </div>

                <div className="form-group-v1">
                  <label>Thank You GIF </label>
                  {thankYouGif && (
                    <div className="thankyou-gif-preview">
                      <img
                        src={s3AssetUrl(S3, thankYouGif)}
                        alt="Thank you"
                        className="thankyou-gif-thumb"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                  <input
                    ref={gifInputRef}
                    type="file"
                    accept=".gif"
                    onChange={handleGifUpload}
                    disabled={isUploadingGif}
                    className="thankyou-gif-input"
                  />
                  {isUploadingGif && (
                    <div className="code">Uploading…</div>
                  )}
                  <div className="code">* Accepts GIF only. Max 5 MB.</div>
                </div>
              </div>
            </div> */}

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Settings
                </div>

                <div className="form-group-v1">
                  <label>Game page title</label>
                  <textarea
                    value={landingPageTitle}
                    onChange={(e) => handleLandingPageTitleChange(e.target.value)}
                    placeholder="Game page title"
                    className="form-control rules-textarea"
                    rows={2}
                    maxLength={LANDING_TITLE_MAX_LEN}
                  />
                  <div className="code">
                    * Maximum {LANDING_TITLE_MAX_LEN} characters.
                  </div>
                </div>

                <div className="form-group-v1">
                  <label>Game page sub title</label>
                  <textarea
                    value={gamePageSubTitle}
                    onChange={(e) => handleGamePageSubTitleChange(e.target.value)}
                    placeholder="Game page sub title"
                    className="form-control rules-textarea"
                    rows={4}
                    maxLength={GAME_SUB_TITLE_MAX_LEN}
                  />
                  <div className="code">
                    * Maximum {GAME_SUB_TITLE_MAX_LEN} characters. Press Enter
                    for a new line (do not type &lt;br&gt; tags).
                  </div>
                </div>

                <h3 className="custom-rules-title">Custom rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <input
                      className="rule-input"
                      type="text"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveRule(i)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <div>
                    <button className="add-btn" onClick={handleAddRule}>
                      + Add New Rule
                    </button>
                  </div>
                  <div className="code">
                    * Keep 7 rules at the most. <br></br>* At least 1 rule is
                    required.<br></br>* Character limit of each rule is 200<br></br>
                  </div>
                </div>

                <h3 className="custom-rules-title">Form Labels</h3>
                <div className="form-group-v1">
                  <label>Name label</label>
                  <input
                    className="rule-input"
                    type="text"
                    value={labelName}
                    onChange={(e) => handleLabelChange(setLabelName)(e.target.value)}
                    placeholder="Your Name"
                    maxLength={LABEL_MAX_LEN}
                  />
                </div>
                <div className="form-group-v1">
                  <label>Colleague name label</label>
                  <input
                    className="rule-input"
                    type="text"
                    value={labelColleagueName}
                    onChange={(e) => handleLabelChange(setLabelColleagueName)(e.target.value)}
                    placeholder="Your Colleagues name"
                    maxLength={LABEL_MAX_LEN}
                  />
                </div>
                <div className="form-group-v1">
                  <label>Colleague email label</label>
                  <input
                    className="rule-input"
                    type="text"
                    value={labelColleagueEmail}
                    onChange={(e) => handleLabelChange(setLabelColleagueEmail)(e.target.value)}
                    placeholder="Your Colleagues email id"
                    maxLength={LABEL_MAX_LEN}
                  />
                </div>
                <div className="form-group-v1">
                  <label>Badge label</label>
                  <input
                    className="rule-input"
                    type="text"
                    value={labelBadge}
                    onChange={(e) => handleLabelChange(setLabelBadge)(e.target.value)}
                    placeholder="Select a Badge"
                    maxLength={LABEL_MAX_LEN}
                  />
                  <div className="code">* Maximum {LABEL_MAX_LEN} characters each.</div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Customization
                </div>
                <div className="form-group-v1 rules-toggle-row">
                  <label>Voting (gallery likes)</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={votingOpen}
                      onChange={handleVotingChange}
                      disabled={savingYogaSettings}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>
                      {savingYogaSettings ? "Saving…" : votingOpen ? "Open" : "Closed"}
                    </span>
                  </div>
                  <div className="code">When closed, like buttons are hidden on the yoga gallery.</div>
                </div>
                {/* <div className="form-group-v1 rules-toggle-row">
                  <label>Player upload</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={uploadOpen}
                      onChange={handleUploadChange}
                      disabled={savingYogaSettings}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>
                      {savingYogaSettings ? "Saving…" : uploadOpen ? "Open" : "Closed"}
                    </span>
                  </div>
                  <div className="code">
                    When closed, players finish poses and go straight to the gallery.
                  </div>
                </div> */}
                {/* {uploadOpen ? (
                  <div className="form-group-v1">
                    <label>Upload count per player</label>
                    <input
                      type="number"
                      min={MIN_UPLOAD_COUNT}
                      max={MAX_UPLOAD_COUNT}
                      value={uploadCount}
                      onChange={(e) => handleUploadCountChange(e.target.value)}
                      onBlur={handleUploadCountBlur}
                      disabled={savingYogaSettings}
                    />
                    <div className="code">
                      * How many photos/videos each player may upload (default 1, max {MAX_UPLOAD_COUNT}).
                    </div>
                  </div>
                ) : null} */}
                <div className="form-group-v1">
                  <button
                    type="button"
                    className="rules-manage-uploads-btn"
                    onClick={() => navigate("/admin/game-uploads")}
                  >
                    Manage posts / uploads
                  </button>
                  <div className="code">
                    Approve, reject, or delete posts for any game. Always available for moderation.
                  </div>
                </div>
                {isUploadModerationGame ? (
                  <div className="form-group-v1 rules-toggle-row">
                    <label>Auto Approve</label>
                    <div className="rules-toggle-control">
                      <Switch
                        checked={!uploadApprovalRequired}
                        onChange={handleAutoApproveChange}
                        onColor="#7c3aed"
                        offColor="#cbd5e1"
                        height={22}
                        width={44}
                      />
                      <span>{uploadApprovalRequired ? "Off" : "On"}</span>
                    </div>
                    <div className="code">
                      When on, new {getGameDisplayName(activeGame)} posts appear in the feed immediately.
                      When off, players see a message that the post will display after admin approval.
                      Approve or reject posts at Manage posts / uploads.
                    </div>
                  </div>
                ) : null}
                <div className="form-group-v1 rules-toggle-row">
                  <label>Badge sharing</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={badgesShareStatus}
                      onChange={setBadgesShareStatus}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>{badgesShareStatus ? "Enabled" : "Disabled"}</span>
                  </div>
                  <div className="code">When disabled, players cannot share badges.</div>
                </div>
                <div className="form-group-v1 rules-toggle-row">
                  <label>Share multiple badges</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={badgesShareMultiple}
                      onChange={setBadgesShareMultiple}
                      disabled={!badgesShareStatus}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>{badgesShareMultiple ? "Yes" : "No"}</span>
                  </div>
                  <div className="code">Allow tagging more than one colleague at a time.</div>
                </div>
                {badgesShareStatus && badgesShareMultiple ? (
                  <div className="form-group-v1">
                    <label>Badge share limit</label>
                    <input
                      type="number"
                      min={MIN_BADGE_SHARE_LIMIT}
                      max={MAX_BADGE_SHARE_LIMIT}
                      value={badgesShareLimit}
                      onChange={(e) => handleBadgeShareLimitChange(e.target.value)}
                      onBlur={handleBadgeShareLimitBlur}
                    />
                    <div className="code">
                      * Minimum {MIN_BADGE_SHARE_LIMIT} and maximum {MAX_BADGE_SHARE_LIMIT} badges per share.
                    </div>
                  </div>
                ) : null}
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button Text Color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_backgroundcolor}
                          onChange={handleColorChange("button_backgroundcolor")}
                        />
                        <label>Button Background Color</label>
                      </div>
                      <div className="color-code">{colors.button_backgroundcolor}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.like_box_text_color}
                          onChange={handleColorChange("like_box_text_color")}
                        />
                        <label>Like Box Text Color</label>
                      </div>
                      <div className="color-code">{colors.like_box_text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.like_box_background_color}
                          onChange={handleColorChange("like_box_background_color")}
                        />
                        <label>Like Box Background Color</label>
                      </div>
                      <div className="color-code">{colors.like_box_background_color}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
           
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
